/**
 * CloseButton Component
 * Reusable close button component with icon from icons folder
 *
 * Best Practice: Uses regular <img> tag for SVG icons instead of Next.js Image
 * because SVGs don't need optimization and load faster from browser cache
 * Supports different sizes and styling variants
 */

import React from 'react';
import { spacing, borders } from '@/design-system/utils';

export interface CloseButtonProps {
  /**
   * Click handler for the close button
   */
  onClick: () => void;
  /**
   * Size of the icon (width and height in pixels)
   * @default 24
   */
  size?: number;
  /**
   * Aria label for accessibility
   * @default 'Close'
   */
  ariaLabel?: string;
  /**
   * Additional CSS class name
   */
  className?: string;
  /**
   * Inline styles for customization
   */
  style?: React.CSSProperties;
  /**
   * Variant style preset
   * - 'default': Transparent background, hover opacity
   * - 'bordered': Border with transparent background
   * - 'filled': Background color with border
   * - 'white': White icon (uses CSS filter to invert colors for white background contexts)
   * @default 'default'
   */
  variant?: 'default' | 'bordered' | 'filled' | 'white';
}

/**
 * CloseButton Component
 * Renders a close button with the close icon from the icons folder
 */
export const CloseButton: React.FC<CloseButtonProps> = ({
  onClick,
  size = 24,
  ariaLabel = 'Close',
  className = '',
  style,
  variant = 'default',
}) => {
  // Base button styles
  const baseStyle: React.CSSProperties = {
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: 'none',
    backgroundColor: 'transparent',
    padding: spacing[2],
    transition: 'opacity 0.2s',
    flexShrink: 0,
    width: 'auto',
    height: 'auto',
    ...style,
  };

  // Variant-specific styles
  const variantStyles: Record<string, React.CSSProperties> = {
    default: {
      backgroundColor: 'transparent',
      border: 'none',
    },
    bordered: {
      border: '0.575px solid rgba(0,0,0,0.15)',
      borderRadius: borders.radius.sm,
      padding: '3.594px',
    },
    filled: {
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
      borderRadius: '50%',
      padding: spacing[0.5],
    },
    white: {
      backgroundColor: 'transparent',
      border: 'none',
    },
  };

  // Icon style for white variant (invert to white)
  const iconStyle: React.CSSProperties =
    variant === 'white'
      ? {
          filter: 'brightness(0) invert(1)',
        }
      : {};

  const buttonStyle: React.CSSProperties = {
    ...baseStyle,
    ...variantStyles[variant],
  };

  const handleMouseEnter = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (variant === 'default' || variant === 'filled') {
      e.currentTarget.style.opacity = '0.8';
    }
  };

  const handleMouseLeave = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (variant === 'default' || variant === 'filled') {
      e.currentTarget.style.opacity = '1';
    }
  };

  return (
    <button
      onClick={onClick}
      style={buttonStyle}
      className={className}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      aria-label={ariaLabel}
      type='button'
    >
      {/* Using regular img tag for SVG icons (best practice) */}
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src='/assets/images/icons/close.svg'
        alt={ariaLabel}
        width={size}
        height={size}
        style={{
          ...iconStyle,
          width: `${size}px`,
          height: `${size}px`,
          minWidth: `${size}px`,
          minHeight: `${size}px`,
          maxWidth: `${size}px`,
          maxHeight: `${size}px`,
          flexShrink: 0,
        }}
      />
    </button>
  );
};
